import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { DatePicker } from './ui/date-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import {
  Truck,
  Plus,
  Trash2,
  Loader2,
  ChevronDown,
  ChevronRight,
  LinkIcon,
} from 'lucide-react';

/**
 * Support Asset Allocation panel — displayed inside the allocation dialog.
 * Allows dispatchers to attach non-billable assets (e.g. flat deck for transport)
 * to a job without affecting billing.
 */
export const SupportAssetPanel = ({ jobId, api, equipment = [], readOnly = false }) => {
  const [supportAssets, setSupportAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ equipment_id: '', start_date: '', end_date: '', notes: '' });

  const fetchSupportAssets = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const res = await api.get(`/allocations/${jobId}/support-assets`);
      setSupportAssets(res.data || []);
      if ((res.data || []).length > 0) setExpanded(true);
    } catch {
      // Silently fail on initial load
    } finally {
      setLoading(false);
    }
  }, [jobId, api]);

  useEffect(() => { fetchSupportAssets(); }, [fetchSupportAssets]);

  const handleAdd = async () => {
    if (!form.equipment_id || !form.start_date) {
      toast.error('Select an asset and start date');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        equipment_id: form.equipment_id,
        start_date: form.start_date,
        end_date: form.end_date || null,
        notes: form.notes || null,
      };
      await api.post(`/allocations/${jobId}/support-asset`, payload);
      toast.success('Support asset added');
      setForm({ equipment_id: '', start_date: '', end_date: '', notes: '' });
      setAdding(false);
      fetchSupportAssets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add support asset');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (allocId) => {
    try {
      await api.delete(`/allocations/${allocId}/support-asset`);
      toast.success('Support asset removed');
      setSupportAssets(prev => prev.filter(a => a.id !== allocId));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove');
    }
  };

  // Only show equipment that isn't the job's primary allocation
  const availableEquipment = equipment;

  return (
    <div className="border border-dashed border-border rounded-lg" data-testid="support-asset-panel">
      <button
        type="button"
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-muted/30 transition-colors rounded-t-lg"
        onClick={() => setExpanded(p => !p)}
        data-testid="support-asset-toggle"
      >
        <div className="flex items-center gap-2">
          <LinkIcon className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground">Support Assets</span>
          {supportAssets.length > 0 && (
            <Badge variant="secondary" className="text-[10px] h-4 px-1">{supportAssets.length}</Badge>
          )}
        </div>
        {expanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-2">
          {loading ? (
            <div className="flex justify-center py-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Existing support assets */}
              {supportAssets.map(sa => (
                <div
                  key={sa.id}
                  className="flex items-center justify-between bg-muted/30 border border-dashed border-border/50 rounded px-3 py-2"
                  data-testid={`support-asset-row-${sa.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Truck className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate">
                        {sa.equipment_name} <span className="text-muted-foreground">({sa.equipment_asset_id})</span>
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {sa.equipment_type} &middot; {sa.start_date}{sa.end_date && sa.end_date !== sa.start_date ? ` \u2192 ${sa.end_date}` : ''}
                        {sa.notes ? ` \u2022 ${sa.notes}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Badge variant="outline" className="text-[9px] border-cyan-500/40 text-cyan-500">Non-billable</Badge>
                    {!readOnly && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                        onClick={() => handleRemove(sa.id)}
                        data-testid={`remove-support-asset-${sa.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}

              {/* Add form */}
              {!readOnly && !adding && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full h-7 text-xs text-muted-foreground hover:text-foreground border border-dashed border-border/50"
                  onClick={() => setAdding(true)}
                  data-testid="add-support-asset-btn"
                >
                  <Plus className="h-3 w-3 mr-1" /> Add Support Asset
                </Button>
              )}

              {!readOnly && adding && (
                <div className="space-y-2 p-2 bg-muted/20 rounded border border-border/50" data-testid="support-asset-form">
                  <div className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground">Asset</Label>
                    <Select value={form.equipment_id} onValueChange={(v) => setForm(f => ({ ...f, equipment_id: v }))}>
                      <SelectTrigger className="h-7 text-xs" data-testid="support-asset-equipment-select">
                        <SelectValue placeholder="Select asset..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {availableEquipment.map(eq => (
                          <SelectItem key={eq.id} value={eq.id} className="text-xs">
                            {eq.name || eq.asset_id} — {eq.equipment_type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">Start Date</Label>
                      <DatePicker
                        value={form.start_date}
                        onChange={(d) => setForm(f => ({ ...f, start_date: d }))}
                        placeholder="Start date"
                        className="h-7 text-xs w-full"
                        testId="support-asset-start"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">End Date</Label>
                      <DatePicker
                        value={form.end_date}
                        onChange={(d) => setForm(f => ({ ...f, end_date: d }))}
                        placeholder="End date"
                        minDate={form.start_date}
                        className="h-7 text-xs w-full"
                        testId="support-asset-end"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground">Notes (optional)</Label>
                    <Input className="h-7 text-xs" placeholder="e.g. Transport only" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} data-testid="support-asset-notes" />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="h-7 text-xs flex-1" onClick={handleAdd} disabled={saving} data-testid="support-asset-save">
                      {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />} Add
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setAdding(false); setForm({ equipment_id: '', start_date: '', end_date: '', notes: '' }); }} data-testid="support-asset-cancel">
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
